import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { Alignment } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  concatStyleSets,
  DirectionalHint,
  VerticalDivider,
} from "@fluentui/react";

import { Stack } from "@1js/acui-stack";

import { smallActionButtonHeight } from "@1js/acui-button";
import {
  ActionControlSize,
  getHoverableTooltipsPreference,
} from "@1js/acui-common";
import { AppFontStylesTwoRowPreviewGalleryButton } from "@1js/acui-gallery-font-styles";
import { getPropsWithMenuDismiss } from "@1js/acui-menu";

import type { ControlRendererFunction } from "../../../controls";
import {
  getRibbonBottomBarStylingConstants,
  getLabelContainerHeight,
  multiRowMaxGap,
} from "../../ribbonStylingConstants";
import type {
  RibbonControlInternalProps,
  RibbonControlInternalUnionProps,
  RibbonControlProps,
} from "../../Controls/RibbonControlProps";
import { getRibbonGroupItemsIcon } from "../RibbonControlGroup/getRibbonGroupItemsIcon";
import type { LayoutChanges } from "../RibbonControlGroup/RibbonControlGroupAnimation";
import { RibbonEnlightenedComponent } from "../RibbonEnlightenedComponent";
import {
  deriveMLRBottomRowKeytipProps,
  deriveMLRMiddleRowKeytipProps,
  deriveMLRTopRowKeytipProps,
} from "./deriveMLRKeytipProps";
import { getRibbonControlGroupItemsProps } from "./getRibbonControlGroupItemsProps";
import { getRibbonControlGroupItemsStyleProps } from "./getRibbonControlGroupItemsStyles";
import {
  getRowDividerClassNames,
  getSectionDividerClassNames,
  vNextStackTokens,
} from "./layoutRenderer.classNames";
import type {
  ControlId,
  Layout,
  MultiRowSectionItem,
  Section,
  ScopedIndexLayout,
  SingleRowSection,
  SingleRowSectionItem,
  VerticalControlStackWithForProps,
  MultiRowSection,
} from "./layoutTypings";
import { getDataLidJsxAttribute, MLRKeytipLocation } from "./layoutTypings";
import type { LayoutDivider } from "..";
import { isNextStyleEnabled } from "@1js/acui-theme";
import { getDefaultLayout, sectionSpacerWidth } from "./layoutHelpers";
import type { AppKeytipProps } from "@1js/acui-keytip";

export type WithKey<T> = T & { key?: string };
type IdToPropsMap = {
  [controlId: string]: WithKey<RibbonControlInternalUnionProps>;
};

const mlrBottomBarStylingConstants =
  getRibbonBottomBarStylingConstants("Multiline");

// This returns the remaining vertical space in the bottom
// bar for the given row count
const remainingBottomBarSpace = (rowCount: number) =>
  mlrBottomBarStylingConstants.bottomBarHeight -
  getLabelContainerHeight() -
  rowCount * smallActionButtonHeight;

// Make the flex-grow correct for multiple rows.
// This tells the flex-grow to make the rows have the multiRowMaxGap space
// between the rows. This automatically works for both two and three row layouts
const multiRowFlexGrowRatio =
  multiRowMaxGap / remainingBottomBarSpace(2 /* rowCount */);

// Make the flex-grow correct for a single
// visible row when there are two total rows
// (so we need to take up and extra small button height)
const singleWithGhostRowFlexGrowRatio =
  smallActionButtonHeight / remainingBottomBarSpace(1 /* rowCount */);

export interface LayoutRendererProps {
  layout?: Layout;
  controlProps: RibbonControlInternalUnionProps[];
  controlRendererFunction: ControlRendererFunction;
  animate: boolean;
  layoutChanges?: LayoutChanges;
  dismissMenu?: () => void;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
}

export const LayoutRenderer: React.FunctionComponent<LayoutRendererProps> = (
  props: LayoutRendererProps
) => {
  const {
    layout,
    controlProps,
    controlRendererFunction,
    animate,
    layoutChanges,
    dismissMenu,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
  } = props;
  const layoutToRender = layout || getDefaultLayout(controlProps);

  const controls = createPropsMap(controlProps);

  // We want to be able to center a control if the group text is longer than the width of the button
  // We will only center if we have a SingleRowSection that doesn't contain any stacked controls.
  const centerControls =
    layoutToRender.sections.length === 1 && // Is there only one section in the group
    Array.isArray(layoutToRender.sections[0]) && // Is the item not a divider
    layoutToRender.sections[0].length === 1 && // Is there only one section of items in the group
    (layoutToRender.sections[0][0] as SingleRowSectionItem[]) !== null && // Are we not a multi-row section
    (layoutToRender.sections[0][0] as SingleRowSectionItem[]).filter((item) =>
      Array.isArray(item)
    ).length === 0; // Are there any stacked controls

  return (
    <Stack
      verticalFill={true}
      horizontal={true}
      style={centerControls ? { justifyContent: "center" } : undefined}
    >
      {layoutToRender.sections.map((section, index) => {
        if (
          typeof section === "object" &&
          (section as LayoutDivider).type !== "Divider"
        ) {
          const sectionRenderer = (sectionToRender: Section) => {
            return (
              <SectionRenderer
                key={`$Section-${index}`}
                section={sectionToRender}
                controls={controls}
                controlRendererFunction={controlRendererFunction}
                animate={animate}
                layoutChanges={layoutChanges}
                dismissMenu={dismissMenu}
                isRibbonSpanning={isRibbonSpanning}
                spanningComponentId={spanningComponentId}
                spanningOffset={spanningOffset}
              />
            );
          };

          if (Array.isArray(section)) {
            return sectionRenderer(section);
          }

          // We know we are rendering a scopedLayout
          const scopedSection = section as ScopedIndexLayout;

          return (
            <div {...getDataLidJsxAttribute(scopedSection.layoutIndex)}>
              {sectionRenderer(scopedSection.controlId as any)}
            </div>
          );
        }

        return (
          <VerticalDivider
            key={`divider${index}`}
            getClassNames={getSectionDividerClassNames}
          />
        );
      })}
    </Stack>
  );
};

interface SectionRendererProps {
  section: Section;
  controls: IdToPropsMap;
  controlRendererFunction: ControlRendererFunction;
  animate: boolean;
  layoutChanges?: LayoutChanges;
  dismissMenu?: () => void;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
}

const SectionRenderer: React.FunctionComponent<SectionRendererProps> = (
  props: SectionRendererProps
) => {
  const {
    section,
    controls,
    controlRendererFunction,
    animate,
    layoutChanges,
    dismissMenu,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
  } = props;

  if (typeof section !== "object" && !Array.isArray(section)) {
    return <React.Fragment />;
  }
  const rowCount = (section as SingleRowSection | MultiRowSection).length;

  // If there's only one row, draw controls Large, if there's multi-row, there's not enough space to render large
  const controlSize =
    rowCount === 1 ? ActionControlSize.Large : ActionControlSize.Small;
  const stackComponent = (
    <Stack
      verticalAlign={"center"}
      tokens={isNextStyleEnabled() ? vNextStackTokens : undefined}
    >
      {(section as SingleRowSection | MultiRowSection).map((row, index) => (
        <RowRenderer
          key={index}
          row={row}
          controls={controls}
          controlRendererFunction={controlRendererFunction}
          defaultControlSize={controlSize}
          keytipLocation={
            index === rowCount - 1 ? MLRKeytipLocation.Bottom : index
          }
          animate={animate}
          layoutChanges={layoutChanges}
          dismissMenu={dismissMenu}
          isConsciousControl={rowCount === 1}
          isRibbonSpanning={isRibbonSpanning}
          spanningComponentId={spanningComponentId}
          spanningOffset={spanningOffset}
        />
      ))}
    </Stack>
  );
  const sectionId = `section${section}`;
  return rowCount > 1 && isRibbonSpanning ? (
    <RibbonEnlightenedComponent
      id={sectionId}
      offset={spanningComponentId === sectionId ? spanningOffset : 0}
    >
      {stackComponent}
    </RibbonEnlightenedComponent>
  ) : (
    stackComponent
  );
};

/**
 * Renders a spacer to add padding between a large control and a stack of small controls
 */
const Spacer = () => (
  <div key="spacer" style={{ width: `${sectionSpacerWidth}px` }} />
);

interface RowRendererProps {
  row:
    | ScopedIndexLayout
    | (ScopedIndexLayout | SingleRowSectionItem)[]
    | MultiRowSectionItem[];
  controls: IdToPropsMap;
  controlRendererFunction: ControlRendererFunction;
  defaultControlSize: ActionControlSize;
  keytipLocation: MLRKeytipLocation;
  animate: boolean;
  layoutChanges?: LayoutChanges;
  dismissMenu?: () => void;
  isConsciousControl?: boolean;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
}

/**
 * Renders a single row whose elements may be:
 *  1. A control ID
 *  2. An array of control IDs to be stacked vertically
 *  3. A divider
 */
const RowRenderer: React.FunctionComponent<RowRendererProps> = (
  props: RowRendererProps
) => {
  const {
    row,
    controls,
    controlRendererFunction,
    defaultControlSize,
    keytipLocation,
    animate,
    layoutChanges,
    dismissMenu,
    isConsciousControl,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
  } = props;
  const rowItems: JSX.Element[] = [];
  let dividerCount = 0;
  let prevItem = undefined;

  // Is this a scopedLayout?
  if (
    typeof row === "object" &&
    !Array.isArray(row) &&
    row.layoutIndex !== undefined
  ) {
    return (
      <div {...getDataLidJsxAttribute(row.layoutIndex)}>
        {RowRenderer({ ...props, row: row.controlId as MultiRowSectionItem[] })}
      </div>
    );
  }

  // At this point, row should be an array
  if (!Array.isArray(row)) {
    return <></>;
  }

  // default to the multi-row flex-grow ratio
  let rowFlexGrow = multiRowFlexGrowRatio;

  for (const item of row) {
    if (
      typeof item === "object" &&
      !Array.isArray(item) &&
      (item as unknown as ScopedIndexLayout).layoutIndex !== undefined
    ) {
      const scopedItem = item as unknown as ScopedIndexLayout;
      rowItems.push(
        <div {...getDataLidJsxAttribute(scopedItem.layoutIndex)}>
          {RowRenderer({
            ...props,
            row: scopedItem.controlId as MultiRowSectionItem[],
          })}
        </div>
      );
    } else if (typeof item === "string") {
      // If the item is not in controls to be rendered.  The control either doesn't exist in the group or has been hidden.
      if (!controls[item]) {
        continue;
      }

      // If the previous item was a stack, add a spacer before this large control
      if (Array.isArray(prevItem)) {
        rowItems.push(<Spacer />);
      }

      // #1 Render the control at the given size
      const itemHasMovedInLayout = layoutChanges && layoutChanges.moving[item];
      if (itemHasMovedInLayout) {
        rowItems.push(
          <div key={item} className="moved">
            <ControlRenderer
              controlId={item}
              controls={controls}
              size={defaultControlSize}
              controlRendererFunction={controlRendererFunction}
              renderOptions={{
                animate,
                keytipLocation,
              }}
              dismissMenu={dismissMenu}
              isConsciousControl={isConsciousControl}
              isRibbonSpanning={isRibbonSpanning}
              spanningComponentId={spanningComponentId}
              spanningOffset={spanningOffset}
            />
          </div>
        );
      } else {
        rowItems.push(
          <ControlRenderer
            key={item}
            controlId={item}
            controls={controls}
            size={defaultControlSize}
            controlRendererFunction={controlRendererFunction}
            renderOptions={{
              animate,
              keytipLocation,
            }}
            dismissMenu={dismissMenu}
            isConsciousControl={isConsciousControl}
            isRibbonSpanning={isRibbonSpanning}
            spanningComponentId={spanningComponentId}
            spanningOffset={spanningOffset}
          />
        );
      }
    } else if (Array.isArray(item)) {
      // Filter out items that cannot be rendered because we don't have props
      const visibleItems = (
        item as (SingleRowSectionItem | MultiRowSectionItem)[]
      ).filter(
        (control: any) => control != null && controls[control]
      ) as string[];
      if (visibleItems.length === 0) {
        continue;
      }

      // If the previous item was a large control, add a spacer before this stack
      if (typeof prevItem === "string") {
        rowItems.push(<Spacer />);
      }

      // use a flex-grow of 1 for stacks by default
      rowFlexGrow = 1;

      // #2 Render the items as a vertical stack (row is a SingleRowSectionItem[] and item is a VerticalControlStack)
      const stackedItems: JSX.Element[] = [];
      let hasMovedControls = false;
      visibleItems.map((stackedControl, layoutRow) => {
        if (layoutChanges && layoutChanges.moving[stackedControl]) {
          hasMovedControls = true;
        }

        stackedItems.push(
          <ControlRenderer
            key={stackedControl}
            controlId={stackedControl}
            controls={controls}
            size={ActionControlSize.Small}
            controlRendererFunction={controlRendererFunction}
            renderOptions={{
              animate,
              keytipLocation: layoutRow,
              stackedItems: visibleItems,
            }}
            dismissMenu={dismissMenu}
          />
        );
      });

      // Attempt to grab ariaProps if the item has them
      const ariaProps = (item as VerticalControlStackWithForProps).ariaProps;
      let verticalAlign: Alignment = "start";

      // We may need to set additional properties
      // if the item length is two
      if (item.length === 2) {
        // If we also have two visible items,
        // we need to center vertically OR if we have
        // one visible item, we need to use the right flex-grow value
        if (visibleItems.length === 2) {
          verticalAlign = "center";
        } else if (visibleItems.length === 1) {
          rowFlexGrow = singleWithGhostRowFlexGrowRatio;
        }
      }

      const rowStackComponent = (
        <Stack
          id={ariaProps && ariaProps.id}
          aria-labelledby={ariaProps && ariaProps.labelledBy}
          role={ariaProps && ariaProps.labelledBy ? "group" : undefined}
          horizontalAlign="start"
          className={hasMovedControls ? "moved" : undefined}
          verticalAlign={verticalAlign}
          key={`controlStack-${JSON.stringify(item)} `}
          tokens={isNextStyleEnabled() ? vNextStackTokens : undefined}
        >
          {stackedItems}
        </Stack>
      );
      const stackId = `controlStack-${JSON.stringify(item)}`;
      rowItems.push(
        isConsciousControl && isRibbonSpanning ? (
          <RibbonEnlightenedComponent
            id={stackId}
            offset={spanningComponentId === stackId ? spanningOffset : 0}
          >
            {rowStackComponent}
          </RibbonEnlightenedComponent>
        ) : (
          rowStackComponent
        )
      );
    } else {
      // #3 Render a vertical bar
      dividerCount += 1;
      rowItems.push(
        <VerticalDivider
          key={`divider${dividerCount}`}
          getClassNames={getRowDividerClassNames}
        />
      );
    }

    prevItem = item;
  }

  return (
    <Stack horizontal={true} style={{ flexGrow: rowFlexGrow }}>
      {rowItems}
    </Stack>
  );
};

type RenderControlOptions = {
  keytipLocation: MLRKeytipLocation;
  animate: boolean;
  stackedItems?: string[];
};

function getTooltipPosition(
  extraUILocation: MLRKeytipLocation
): DirectionalHint | undefined {
  // If we are using hoverable tooltips, set tooltip location based on position in MLR
  if (getHoverableTooltipsPreference()) {
    return extraUILocation === MLRKeytipLocation.Top
      ? DirectionalHint.topCenter
      : DirectionalHint.bottomCenter;
  }
  return undefined;
}

interface ControlRendererProps {
  controlId: ControlId;
  controls: IdToPropsMap;
  size: ActionControlSize;
  controlRendererFunction: ControlRendererFunction;
  renderOptions: RenderControlOptions;
  dismissMenu?: () => void;
  isConsciousControl?: boolean;
  isRibbonSpanning?: boolean;
  spanningComponentId?: string;
  spanningOffset?: number;
}

/** Renders a control at a given size with a key */

const ControlRenderer: React.FunctionComponent<ControlRendererProps> = (
  props: ControlRendererProps
) => {
  const {
    controlId,
    controls,
    size,
    controlRendererFunction,
    renderOptions,
    dismissMenu,
    isConsciousControl,
    isRibbonSpanning,
    spanningComponentId,
    spanningOffset,
  } = props;
  const controlProps = controls[controlId];

  const propsToRender = {
    // This line works without a cast to "any" but brings with it a 4x longer compile time.
    // This compile time issue was exposed with a TS4.1 "complex union" error and should eventually be fixed.
    ...(getPropsWithMenuDismissInternal(
      controlProps as RibbonControlProps,
      dismissMenu
    ) as any),
    size,
    key: `${controlId}-${size}`,
    animate: renderOptions.animate,
    downChevronIconSize: "icon8",
    keytipProps: getAugmentedKeytipProps(
      controlProps,
      renderOptions.keytipLocation,
      !!renderOptions.stackedItems
    ),
    tooltipDirection:
      controlProps.tooltipDirection ||
      getTooltipPosition(renderOptions.keytipLocation),
    ...useGetPropsBasedOnSize(controlProps as RibbonControlProps, size),
    ...getRibbonControlGroupItemsProps(
      controlProps as RibbonControlProps,
      size
    ),
  } as RibbonControlInternalProps;

  const control = controlRendererFunction({
    ...propsToRender,
    ...getLayoutProps(propsToRender, controls, renderOptions),
  } as RibbonControlInternalProps);

  return isConsciousControl && isRibbonSpanning ? (
    <RibbonEnlightenedComponent
      id={controlId}
      offset={spanningComponentId === controlId ? spanningOffset : 0}
    >
      {control}
    </RibbonEnlightenedComponent>
  ) : (
    control
  );
};

function createPropsMap(
  controls: WithKey<RibbonControlInternalUnionProps>[]
): IdToPropsMap {
  const map: IdToPropsMap = {};

  for (const control of controls) {
    map[control.id] = control;
  }

  return map;
}

function useGetPropsBasedOnSize(
  props: RibbonControlProps,
  size: ActionControlSize
) {
  // If a Large button does not set the hideLabel property, that means we will always show a label
  // so we can safely set hideLabel to false.
  // This will prevent us from ever showing a tooltip where the tooltip matches the label text.
  const hideLabel =
    size === ActionControlSize.Large ? { hideLabel: false } : {};
  return {
    ...hideLabel,
    icon: getRibbonGroupItemsIcon(props, size),
    ...getRibbonControlGroupItemsStyleProps(props, size),
    getGalleryButtonComponent:
      props.type === "AppFontStylesPreviewGalleryProps" &&
      size === ActionControlSize.Large
        ? AppFontStylesTwoRowPreviewGalleryButton
        : undefined,
  };
}

function getLayoutProps(
  props: RibbonControlInternalProps,
  controls: IdToPropsMap,
  renderOptions: RenderControlOptions
) {
  if (props.type === "AppComboBoxProps" && renderOptions.stackedItems) {
    const allStackedItemsAreComboBoxes = renderOptions.stackedItems.every(
      (item) => controls[item] && controls[item].type === "AppComboBoxProps"
    );
    if (allStackedItemsAreComboBoxes) {
      // When a stack consists of only combo boxes, align their control boxes
      return {
        styles: concatStyleSets(props.styles, {
          controlContainer: {
            width: "100%",
            flexGrow: multiRowFlexGrowRatio,
          },
          controlAndIconContainer: {
            width: "100%",
            flex: 1,
          },
          container: { width: "100%" },
          label: { flex: 1, width: "100%" },
        }),
      };
    }
    // If a combo box is in a stack and has other controls, fill the container
    return {
      styles: concatStyleSets(props.styles, {
        container: { width: "100%" },
      }),
    };
  }

  return {};
}

function getAugmentedKeytipProps(
  controlProps: RibbonControlInternalUnionProps,
  keytipLocation: MLRKeytipLocation,
  isVerticalStackElement?: boolean
): AppKeytipProps {
  switch (controlProps.type) {
    case "AppButtonProps":
    case "AppInkButtonProps":
    case "AppToggleButtonProps":
    case "AppFlyoutAnchorProps":
    case "AppInkFlyoutAnchorProps":
    case "AppSplitButtonProps":
    case "AppComboBoxProps":
    case "AppSpinButtonProps":
    case "AppCheckboxProps":
    case "RibbonTableStylesPreviewGalleryProps":
    case "AppFontStylesPreviewGalleryProps":
    case "AppIconPreviewGalleryProps":
    case "AppFontPickerProps":
    case "RibbonChangeColorsPreviewGalleryProps":
    case "AppPromptInputProps":
    case "AppSuggestionProps": {
      if (controlProps.keytipProps) {
        switch (keytipLocation) {
          case MLRKeytipLocation.Top:
            return deriveMLRTopRowKeytipProps(
              controlProps.keytipProps,
              isVerticalStackElement
            );
          case MLRKeytipLocation.Middle:
            return deriveMLRMiddleRowKeytipProps(
              controlProps.keytipProps,
              isVerticalStackElement
            );
          case MLRKeytipLocation.Bottom:
            return deriveMLRBottomRowKeytipProps(
              controlProps.keytipProps,
              isVerticalStackElement
            );
        }
      }
    }
  }
  return undefined;
}

function getPropsWithMenuDismissInternal(
  props: RibbonControlProps,
  dismissMenu?: () => void
) {
  switch (props.type) {
    case "AppButtonProps":
    case "AppToggleButtonProps":
    case "AppSplitButtonProps":
    case "AppCheckboxProps": {
      return dismissMenu ? getPropsWithMenuDismiss(props, dismissMenu) : props;
    }
  }
  return props;
}
